/**
 * Aux Sensor tab — channel configuration.
 *
 * The tab compares same-type sensors (e.g. two power meters) against each other.
 * The source is the durable **gridded** data (`data_gridded`), fetched via the
 * `grid_data_aux_sensor` scope, where the pipeline already exposes the primary
 * and secondary selections of each type on the shared `TimeRel_tG00` axis:
 *
 *   Power → RiderPwr_P (primary) / RiderAltPwr_P (secondary)
 *   Speed → WhlRspd_n  (primary) / WhlRspd_alt_n (secondary)
 *
 * Wheel speed can be shown rotational (raw rev/s) or linear (× wheel
 * circumference → km/h), and in linear mode GPS speed is overlaid for reference.
 * Dynamic pressure is an inactive stub for now.
 *
 * This module is pure (no React / network) so it can be unit-tested and reused.
 */
import { RunHealthObj } from 'types/Run';

export type AuxSensorType = 'power' | 'speed' | 'wind';
export type SpeedMode = 'rotational' | 'linear';
export type DataMode = 'all' | 'traverse';

/** Shared relative-time axis for every gridded series. */
export const AUX_TIME_CHANNEL = 'TimeRel_tG00';

/** Windowed traverse lap channel: 0 outside a traverse, N within traverse N. */
export const TRAVERSE_CHANNEL = 'TraverseLap_NG10';

/** GPS speed gridded channels (m/s); first present wins. */
export const GPS_SPEED_CHANNELS = ['GpsSpeed_vG20', 'GpsSpeed_vG10'];

const MS_TO_KMH = 3.6;

export interface AuxSeriesDef {
  key: string;
  label: string;
  channel: string;
}

/** A plotted series — points are `[relativeTimeSeconds, value]` (null = gap). */
export interface AuxPlotSeries {
  key: string;
  label: string;
  points: [number, number | null][];
}

export interface AuxPlotDef {
  type: AuxSensorType;
  title: string;
  unit: string;
  /** false → render an inactive placeholder (no data wired yet). */
  active: boolean;
  series: AuxSeriesDef[];
}

export const POWER_PLOT: AuxPlotDef = {
  type: 'power',
  title: 'Power Meter',
  unit: 'W',
  active: true,
  series: [
    { key: 'power-primary', label: 'Power Meter 1', channel: 'RiderPwr_P' },
    { key: 'power-secondary', label: 'Power Meter 2', channel: 'RiderAltPwr_P' },
  ],
};

export const WIND_PLOT: AuxPlotDef = {
  type: 'wind',
  title: 'Dynamic Pressure',
  unit: 'Pa',
  active: false,
  series: [],
};

// Wheel-speed channels (rotational, rev/s). Linear mode scales by circumference.
const SPEED_SERIES: AuxSeriesDef[] = [
  { key: 'speed-primary', label: 'Wheel Speed 1', channel: 'WhlRspd_n' },
  { key: 'speed-secondary', label: 'Wheel Speed 2', channel: 'WhlRspd_alt_n' },
];

/** All gridded channels the tab needs — kept in sync with the server scope. */
export const AUX_SENSOR_GRID_CHANNELS = [
  AUX_TIME_CHANNEL,
  TRAVERSE_CHANNEL,
  ...POWER_PLOT.series.map((s) => s.channel),
  ...SPEED_SERIES.map((s) => s.channel),
  ...GPS_SPEED_CHANNELS,
];

type Gridded = Record<string, (number | null)[]> | null | undefined;

/**
 * Zip a value channel against the shared time channel into `[time, value]`
 * points, optionally scaling each value. `TimeRel_tG00` is already relative
 * seconds, so no time normalisation is needed. NaN/null samples are skipped.
 * Returns [] if the channel is absent or entirely empty.
 */
interface SeriesOpts {
  transform?: (v: number) => number;
  /** When set, samples failing the predicate become gaps (null) not points. */
  inWindow?: (i: number) => boolean;
}

export function buildSeriesPoints(
  gridded: Gridded,
  channel: string,
  opts: SeriesOpts = {},
): [number, number | null][] {
  if (!gridded) return [];
  const time = gridded[AUX_TIME_CHANNEL];
  const values = gridded[channel];
  if (!time || !values) return [];
  const { transform, inWindow } = opts;
  const gapped = !!inWindow;
  const n = Math.min(time.length, values.length);
  const points: [number, number | null][] = [];
  for (let i = 0; i < n; i += 1) {
    const t = time[i];
    if (t == null || Number.isNaN(t)) continue;
    const masked = inWindow ? !inWindow(i) : false;
    const v = values[i];
    if (masked || v == null || Number.isNaN(v)) {
      if (gapped) points.push([t, null]);
      continue;
    }
    points.push([t, transform ? transform(v) : v]);
  }
  return points;
}

/** Predicate: is sample i inside a traverse window (TraverseLap_NG10 > 0)? */
export function traverseMask(gridded: Gridded): ((i: number) => boolean) | undefined {
  const lap = gridded?.[TRAVERSE_CHANNEL];
  if (!lap) return undefined;
  return (i: number) => {
    const v = lap[i];
    return v != null && !Number.isNaN(v) && v > 0;
  };
}

export function hasTraverseData(gridded: Gridded): boolean {
  const lap = gridded?.[TRAVERSE_CHANNEL];
  return !!lap && lap.some((v) => v != null && !Number.isNaN(v) && v > 0);
}

export interface TraverseSegment {
  lap: number;
  startS: number;
  endS: number;
}

/** Contiguous nonzero runs of TraverseLap_NG10, with TimeRel start/end (s). */
export function buildTraverseSegments(gridded: Gridded): TraverseSegment[] {
  const time = gridded?.[AUX_TIME_CHANNEL];
  const lap = gridded?.[TRAVERSE_CHANNEL];
  const segments: TraverseSegment[] = [];
  if (!time || !lap) return segments;
  const n = Math.min(time.length, lap.length);
  let cur = 0;
  let startS = 0;
  let lastT = 0;
  for (let i = 0; i < n; i += 1) {
    const t = time[i];
    if (t == null || Number.isNaN(t)) continue;
    const raw = lap[i];
    const v = raw != null && !Number.isNaN(raw) && raw > 0 ? raw : 0;
    if (v !== cur) {
      if (cur > 0) segments.push({ lap: cur, startS, endS: lastT });
      cur = v;
      startS = t;
    }
    lastT = t;
  }
  if (cur > 0) segments.push({ lap: cur, startS, endS: lastT });
  return segments;
}

function firstPresentChannel(gridded: Gridded, channels: string[]): string | null {
  if (!gridded) return null;
  for (const ch of channels) {
    const arr = gridded[ch];
    if (arr && arr.some((v) => v != null && !Number.isNaN(v))) return ch;
  }
  return null;
}

const hasValue = (s: AuxPlotSeries) => s.points.some((p) => p[1] != null);

export function buildPowerSeries(gridded: Gridded, dataMode: DataMode = 'all'): AuxPlotSeries[] {
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;
  return POWER_PLOT.series
    .map((s) => ({ key: s.key, label: s.label, points: buildSeriesPoints(gridded, s.channel, { inWindow }) }))
    .filter(hasValue);
}

/**
 * Wheel-speed series. Rotational → raw rev/s. Linear → rev/s × wheel
 * circumference (m) × 3.6 = km/h, with GPS speed (m/s × 3.6) overlaid. In
 * traverse mode, samples outside a traverse window are masked to gaps.
 */
export function buildSpeedSeries(
  gridded: Gridded,
  mode: SpeedMode,
  circumference: number,
  dataMode: DataMode = 'all',
): AuxPlotSeries[] {
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;
  if (mode === 'rotational') {
    return SPEED_SERIES
      .map((s) => ({ key: s.key, label: s.label, points: buildSeriesPoints(gridded, s.channel, { inWindow }) }))
      .filter(hasValue);
  }
  const toKmh = (v: number) => v * circumference * MS_TO_KMH;
  const series: AuxPlotSeries[] = SPEED_SERIES.map((s) => ({
    key: s.key,
    label: s.label,
    points: buildSeriesPoints(gridded, s.channel, { transform: toKmh, inWindow }),
  }));
  const gpsChannel = firstPresentChannel(gridded, GPS_SPEED_CHANNELS);
  if (gpsChannel) {
    series.push({
      key: 'speed-gps',
      label: 'GPS Speed',
      points: buildSeriesPoints(gridded, gpsChannel, { transform: (v) => v * MS_TO_KMH, inWindow }),
    });
  }
  return series.filter(hasValue);
}

export function speedUnit(mode: SpeedMode): string {
  return mode === 'linear' ? 'km/h' : 'rev/s';
}

/** One scatter series for the comparison plot. */
export interface AuxScatterSeries {
  key: string;
  name: string;
  points: [number, number][];
}

export interface AuxComparison {
  series: AuxScatterSeries[];
  /** Upper bound for the (square) axes and the 1:1 reference line. */
  axisMax: number;
}

/**
 * Wheel-sensor agreement scatter. X = wheel speed 1; Y = wheel speed 2 and GPS
 * speed (both vs sensor 1). Everything is converted to km/h so the wheel sensors
 * and GPS are directly comparable; points on the 1:1 diagonal mean agreement.
 * Channels are index-aligned on the shared grid, so samples zip by index.
 */
export function buildComparisonScatter(
  gridded: Gridded,
  circumference: number,
  dataMode: DataMode = 'all',
): AuxComparison {
  const toKmh = (v: number) => v * circumference * MS_TO_KMH;
  const w1 = gridded?.WhlRspd_n;
  const w2 = gridded?.WhlRspd_alt_n;
  const gpsChannel = firstPresentChannel(gridded, GPS_SPEED_CHANNELS);
  const gps = gpsChannel && gridded ? gridded[gpsChannel] : undefined;
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;

  const vsW2: [number, number][] = [];
  const vsGps: [number, number][] = [];
  let axisMax = 0;

  if (w1) {
    for (let i = 0; i < w1.length; i += 1) {
      const x = w1[i];
      if (x == null || Number.isNaN(x)) continue;
      if (inWindow && !inWindow(i)) continue;
      const xk = toKmh(x);
      const w2v = w2?.[i];
      if (w2v != null && !Number.isNaN(w2v)) {
        const yk = toKmh(w2v);
        vsW2.push([xk, yk]);
        axisMax = Math.max(axisMax, xk, yk);
      }
      const gpsv = gps?.[i];
      if (gpsv != null && !Number.isNaN(gpsv)) {
        const yk = gpsv * MS_TO_KMH;
        vsGps.push([xk, yk]);
        axisMax = Math.max(axisMax, xk, yk);
      }
    }
  }

  const series: AuxScatterSeries[] = [];
  if (vsW2.length) series.push({ key: 'cmp-w2', name: 'Wheel Speed 2', points: vsW2 });
  if (vsGps.length) series.push({ key: 'cmp-gps', name: 'GPS Speed', points: vsGps });
  return { series, axisMax: Math.ceil(axisMax) };
}

/** Power-meter agreement scatter: x = RiderPwr_P, y = RiderAltPwr_P (both W). */
export function buildPowerComparisonScatter(gridded: Gridded, dataMode: DataMode = 'all'): AuxComparison {
  const p1 = gridded?.RiderPwr_P;
  const p2 = gridded?.RiderAltPwr_P;
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;
  const points: [number, number][] = [];
  let axisMax = 0;
  if (p1 && p2) {
    const n = Math.min(p1.length, p2.length);
    for (let i = 0; i < n; i += 1) {
      if (inWindow && !inWindow(i)) continue;
      const a = p1[i];
      const b = p2[i];
      if (a == null || b == null || Number.isNaN(a) || Number.isNaN(b)) continue;
      points.push([a, b]);
      axisMax = Math.max(axisMax, a, b);
    }
  }
  const series: AuxScatterSeries[] = points.length
    ? [{ key: 'pwr-cmp', name: 'Power Meter 2', points }]
    : [];
  return { series, axisMax: Math.ceil(axisMax) };
}

export interface RatioForceData {
  /** [contactPatchForceN, wheelSpeed1 / wheelSpeed2] points. */
  points: [number, number][];
  /** Max wheel-1 linear speed (m/s) across the run — slider upper bound. */
  maxSpeedMs: number;
}

/**
 * Slip-vs-tractive-force scatter for the two wheel sensors.
 *   x = contact patch (tractive) force = RiderPwr_P / (WhlRspd_n × circumference)
 *       — power / ground-speed = N, since P = F·v.
 *   y = WhlRspd_n / WhlRspd_alt_n — the slip ratio between the two sensors.
 * Samples whose sensor-1 linear speed is below `speedThresholdMs` are masked:
 * at very low speed both the force (÷v) and the ratio explode into noise.
 */
export function buildRatioForceScatter(
  gridded: Gridded,
  circumference: number,
  speedThresholdMs: number,
  dataMode: DataMode = 'all',
): RatioForceData {
  const w1 = gridded?.WhlRspd_n;
  const w2 = gridded?.WhlRspd_alt_n;
  const pwr = gridded?.RiderPwr_P;
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;
  const points: [number, number][] = [];
  let maxSpeedMs = 0;

  if (w1 && w2 && pwr) {
    const n = Math.min(w1.length, w2.length, pwr.length);
    for (let i = 0; i < n; i += 1) {
      const r1 = w1[i];
      const r2 = w2[i];
      const p = pwr[i];
      if (r1 == null || r2 == null || p == null) continue;
      if (Number.isNaN(r1) || Number.isNaN(r2) || Number.isNaN(p)) continue;
      const speedMs = r1 * circumference;
      if (speedMs > maxSpeedMs) maxSpeedMs = speedMs;
      if (inWindow && !inWindow(i)) continue;
      if (speedMs < speedThresholdMs || speedMs === 0 || r2 === 0) continue;
      points.push([p / speedMs, r1 / r2]);
    }
  }
  return { points, maxSpeedMs: Math.ceil(maxSpeedMs) };
}

/** Max wheel-1 linear speed (m/s) over the run — the slip-ratio slider's range. */
export function maxWheelSpeedMs(gridded: Gridded, circumference: number): number {
  const w1 = gridded?.WhlRspd_n;
  if (!w1) return 0;
  let max = 0;
  for (const v of w1) {
    if (v == null || Number.isNaN(v)) continue;
    const speedMs = v * circumference;
    if (speedMs > max) max = speedMs;
  }
  return Math.ceil(max);
}

/**
 * Slip ratio (WhlRspd_n / WhlRspd_alt_n) versus time. Returns one point per grid
 * sample with y = null where the sensor-1 linear speed is below the threshold, so
 * the masked low-speed stretches render as line gaps rather than bridging.
 */
export function buildRatioTimeSeries(
  gridded: Gridded,
  circumference: number,
  speedThresholdMs: number,
  dataMode: DataMode = 'all',
): [number, number | null][] {
  const time = gridded?.[AUX_TIME_CHANNEL];
  const w1 = gridded?.WhlRspd_n;
  const w2 = gridded?.WhlRspd_alt_n;
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;
  const points: [number, number | null][] = [];
  if (time && w1 && w2) {
    const n = Math.min(time.length, w1.length, w2.length);
    for (let i = 0; i < n; i += 1) {
      const t = time[i];
      if (t == null || Number.isNaN(t)) continue;
      const r1 = w1[i];
      const r2 = w2[i];
      let ratio: number | null = null;
      const inTraverse = !inWindow || inWindow(i);
      if (inTraverse && r1 != null && r2 != null && !Number.isNaN(r1) && !Number.isNaN(r2) && r2 !== 0) {
        if (r1 * circumference >= speedThresholdMs) ratio = r1 / r2;
      }
      points.push([t, ratio]);
    }
  }
  return points;
}

/**
 * Whether a run has more than one of any aux sensor type, i.e. whether the
 * "Aux Sensor" tab should be shown. Reads `run_health_obj.sensors_present`,
 * whose `power_meter_2/_3` and `speed_2/_3` keys appear only when those
 * supplementary sensors were detected — no extra network call required.
 */
export function hasAuxSensors(
  run: { run_health_obj?: RunHealthObj | null } | null | undefined,
): boolean {
  const sp = run?.run_health_obj?.sensors_present as Record<string, unknown> | undefined;
  if (!sp) return false;
  return ['power_meter_2', 'power_meter_3', 'speed_2', 'speed_3', 'wind_2', 'wind_3', 'aero_2', 'aero_3']
    .some((key) => !!sp[key]);
}
